import React, { useState } from 'react';
import { Eye, Edit2, Mail, FileText, DollarSign, Download } from 'lucide-react';
import { colors } from '../../styles/colors';
import StatusDropdown from './StatusDropdown';
import { formatEuro } from '../../utils/formatters';
import { DatePicker } from '../ui/DatePicker';

// Fonctions utilitaires locales
function formatMoisAnnee(mois, annee) {
  if (!mois || !annee) return '-';
  const date = new Date(annee, mois - 1);
  return date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function QontoStatusBadge({ status, einvoicingStatus, errorMessage }) {
  if (errorMessage) {
    return (
      <span title={errorMessage} style={{
        display: 'inline-block', marginTop: '0.25rem', padding: '0.1rem 0.5rem',
        borderRadius: '0.75rem', fontSize: '0.7rem', fontWeight: '500',
        backgroundColor: '#fde8e8', color: '#c0392b', cursor: 'help'
      }}>
        Qonto : erreur
      </span>
    );
  }

  if (!status) return null;

  const label = einvoicingStatus
    ? `Qonto : ${einvoicingStatus}`
    : `Qonto : ${status}`;

  return (
    <span style={{
      display: 'inline-block', marginTop: '0.25rem', padding: '0.1rem 0.5rem',
      borderRadius: '0.75rem', fontSize: '0.7rem', fontWeight: '500',
      backgroundColor: '#e8f6f3', color: '#0f766e'
    }}>
      {label}
    </span>
  );
}

function formatPercentage(value) {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/**
 * FactureRow - Ligne de facture dans le tableau (externe ou interne)
 *
 * Composant unifié qui gère à la fois les factures externes et internes
 * en adaptant les colonnes et boutons d'action selon le type.
 *
 * @param {Object} props
 * @param {Object} props.facture - Données de la facture (objet Airtable)
 * @param {number} props.index - Index pour alternance de couleur
 * @param {'externe'|'interne'} props.type - Type de facture
 * @param {Object} props.handlers - Handlers d'actions (onEdit, onSendEmail, etc.)
 * @param {Object} props.permissions - Permissions utilisateur
 * @param {Object} props.maps - Maps prestataires/consultants
 */
function FactureRow({ facture, index, type, handlers, permissions, maps }) {
  const [editingPayment, setEditingPayment] = useState(null);
  const fields = facture.fields || {};

  // Données communes
  const numero = fields['N° facture'] || '-';
  const statut = fields.Statut || 'Brouillon';
  const dateEmission = fields['Date émission'];
  const datePaiement = fields['Date paiement'];
  const montantHT = fields['Montant total (HT)'] || 0;
  const tauxTVA = fields['Taux TVA (%)'] || 0;
  const montantTVA = fields.TVA || 0;
  const montantTTC = fields['Montant total (TTC)'] || 0;

  // Données spécifiques selon le type
  const periode = type === 'externe'
    ? formatMoisAnnee(fields.Mois, fields.Année)
    : `${fields.Trimestre || ''} ${fields.Année || ''}`.trim() || '-';

  const tiersId = type === 'externe'
    ? fields['Prestataire porteur']?.[0]
    : fields.Consultant?.[0];

  const tiersNom = type === 'externe'
    ? maps.prestataires[tiersId]?.nom || '-'
    : maps.consultants[tiersId]?.nom || '-';

  // Client (destinataire) de la facture interne : un prestataire spécifique choisi
  // manuellement, ou "EPHYCO" par défaut (aucun prestataire_id renseigné).
  const prestataireDestinataireId = type === 'interne' ? fields.prestataire_id : null;
  const clientNom = type === 'interne'
    ? (prestataireDestinataireId ? (maps.prestataires[prestataireDestinataireId]?.nom || '-') : 'EPHYCO')
    : null;

  const resteAPayer = type === 'interne' ? (fields['Reste à payer'] || 0) : null;
  const montantPaye = type === 'interne' ? (montantTTC - resteAPayer) : null;
  const driveFileId = type === 'interne' ? fields.DriveFileId : null;

  const qontoStatus = fields.QontoStatus;
  const qontoEinvoicingStatus = fields.QontoEinvoicingStatus;
  const qontoErrorMessage = fields.QontoErrorMessage;

  const { canEditFacturation, canEditFacturationStatus } = permissions;

  const handleDatePaiementChange = (newDate) => {
    handlers.onDatePaiementChange(facture, newDate);
    setEditingPayment(null);
  };

  return (
    <tr
      style={{
        backgroundColor: index % 2 === 0 ? colors.text.white : colors.secondary.lightGray,
        borderBottom: `1px solid ${colors.secondary.lightGray}`
      }}
    >
      {/* N° Facture */}
      <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: colors.primary.teal }}>
        {numero}
      </td>

      {/* Période */}
      <td style={{ padding: '0.75rem 1rem', color: colors.primary.gray }}>
        {periode}
      </td>

      {/* Tiers (Prestataire ou Consultant) */}
      <td style={{ padding: '0.75rem 1rem', color: colors.primary.gray }}>
        {tiersNom}
      </td>

      {/* Client (destinataire, factures internes uniquement) */}
      {type === 'interne' && (
        <td style={{ padding: '0.75rem 1rem', color: colors.primary.gray }}>
          {clientNom}
        </td>
      )}

      {/* Montant HT */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: colors.primary.gray }}>
        {formatEuro(montantHT)}
      </td>

      {/* Taux TVA */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: colors.primary.gray }}>
        {formatPercentage(tauxTVA)}
      </td>

      {/* Montant TVA */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: colors.primary.gray }}>
        {formatEuro(montantTVA)}
      </td>

      {/* Montant TTC */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: colors.primary.gray }}>
        {formatEuro(montantTTC)}
      </td>

      {/* Montant payé (factures internes uniquement) */}
      {type === 'interne' && (
        <td style={{
          padding: '0.75rem 1rem',
          textAlign: 'right',
          fontWeight: '600',
          color: montantPaye > 0 ? colors.status.success : colors.primary.gray
        }}>
          {montantPaye > 0 ? formatEuro(montantPaye) : '-'}
        </td>
      )}

      {/* Reste à payer (factures internes uniquement) */}
      {type === 'interne' && (
        <td style={{
          padding: '0.75rem 1rem',
          textAlign: 'right',
          fontWeight: '600',
          color: resteAPayer > 0 ? colors.primary.gold : colors.primary.teal
        }}>
          {formatEuro(resteAPayer)}
        </td>
      )}

      {/* Date d'émission */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: colors.primary.gray }}>
        {formatDate(dateEmission)}
      </td>

      {/* Statut */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
        <StatusDropdown
          currentStatus={statut}
          onStatusChange={(newStatus) => handlers.onStatutChange(facture, newStatus)}
          canEdit={canEditFacturationStatus}
        />
        {type === 'externe' && (
          <div>
            <QontoStatusBadge
              status={qontoStatus}
              einvoicingStatus={qontoEinvoicingStatus}
              errorMessage={qontoErrorMessage}
            />
          </div>
        )}
      </td>

      {/* Date de paiement */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
        {statut === 'Payée' ? (
          editingPayment === facture.id && canEditFacturation ? (
            <DatePicker
              defaultValue={datePaiement}
              onBlur={(v) => handleDatePaiementChange(v)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditingPayment(null);
              }}
              autoFocus
              size="sm"
              style={{
                border: `1px solid ${colors.primary.teal}`,
              }}
            />
          ) : (
            <span
              onClick={() => canEditFacturation && setEditingPayment(facture.id)}
              style={{
                cursor: !canEditFacturation ? 'not-allowed' : 'pointer',
                color: colors.primary.gray,
                opacity: !canEditFacturation ? 0.6 : 1
              }}
              title={!canEditFacturation ? "Vous n'avez pas les permissions pour modifier la date" : "Cliquer pour modifier"}
            >
              {formatDate(datePaiement)}
            </span>
          )
        ) : (
          <span style={{ color: colors.secondary.mediumGray }}>-</span>
        )}
      </td>

      {/* Actions */}
      <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          {type === 'externe' ? (
            <>
              {/* Bouton Éditer/Voir (Externe) */}
              {(() => {
                const isReadOnly = statut === 'Emise' || statut === 'Payée';
                const canEdit = canEditFacturation || isReadOnly;
                const tooltipText = !canEditFacturation && !isReadOnly
                  ? "Vous n'avez pas les permissions pour éditer"
                  : isReadOnly
                  ? "Voir les détails de la facture"
                  : "Éditer les détails de la facture";

                return (
                  <button
                    onClick={() => canEdit && handlers.onOpenDetail(facture, isReadOnly || !canEditFacturation)}
                    disabled={!canEdit}
                    title={tooltipText}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: !canEdit ? 'not-allowed' : 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.primary.teal,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: !canEdit ? 0.4 : 1
                    }}
                    onMouseEnter={(e) => canEdit && (e.currentTarget.style.backgroundColor = colors.secondary.lightTeal)}
                    onMouseLeave={(e) => canEdit && (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {isReadOnly || !canEditFacturation ? <Eye size={18} /> : <Edit2 size={18} />}
                  </button>
                );
              })()}

              {/* Bouton Envoyer Email (Externe) */}
              {(() => {
                const canSendEmail = canEditFacturation && statut === 'A envoyer';
                const tooltipText = !canEditFacturation
                  ? "Vous n'avez pas les permissions pour envoyer des emails"
                  : statut !== 'A envoyer'
                  ? "La facture doit être validée (statut 'A envoyer') pour être envoyée"
                  : "Envoyer par email";

                return (
                  <button
                    onClick={() => canSendEmail && handlers.onSendEmail(facture)}
                    disabled={!canSendEmail}
                    title={tooltipText}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: !canSendEmail ? 'not-allowed' : 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.primary.gold,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: !canSendEmail ? 0.4 : 1
                    }}
                    onMouseEnter={(e) => canSendEmail && (e.currentTarget.style.backgroundColor = colors.background.paleYellow)}
                    onMouseLeave={(e) => canSendEmail && (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <Mail size={18} />
                  </button>
                );
              })()}

              {/* Bouton Voir PDF (Externe - ReadOnly) */}
              {(() => {
                const isReadOnly = statut === 'Emise' || statut === 'Payée';

                return isReadOnly ? (
                  <button
                    onClick={() => handlers.onViewPdf(numero)}
                    title="Voir le PDF depuis Google Drive"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.status.info,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.background.paleBlue}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <FileText size={18} />
                  </button>
                ) : null;
              })()}

              {/* Bouton Marquer comme Payée (Externe) */}
              {(() => {
                const canMarkAsPaid = statut === 'Emise';

                return canMarkAsPaid ? (
                  <button
                    onClick={() => handlers.onMarkAsPaid(facture)}
                    title="Marquer comme payée"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.status.success,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.background.paleGreen}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <DollarSign size={18} />
                  </button>
                ) : null;
              })()}
            </>
          ) : (
            <>
              {/* Bouton Éditer (Interne) */}
              {(() => {
                const canEdit = canEditFacturation && (statut === 'Brouillon' || statut === 'A envoyer');
                const tooltipText = !canEditFacturation
                  ? "Vous n'avez pas les permissions pour éditer"
                  : !canEdit
                  ? "Seules les factures Brouillon ou A envoyer peuvent être éditées"
                  : "Éditer la facture";

                return (
                  <button
                    onClick={() => canEdit && handlers.onEditInterne(facture)}
                    disabled={!canEdit}
                    title={tooltipText}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: !canEdit ? 'not-allowed' : 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.primary.teal,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: !canEdit ? 0.4 : 1
                    }}
                    onMouseEnter={(e) => canEdit && (e.currentTarget.style.backgroundColor = colors.secondary.lightTeal)}
                    onMouseLeave={(e) => canEdit && (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <Edit2 size={18} />
                  </button>
                );
              })()}

              {/* Bouton Voir PDF Drive (Interne) */}
              {driveFileId && (
                <button
                  onClick={() => window.open(`https://drive.google.com/file/d/${driveFileId}/view`, '_blank')}
                  title="Consulter le PDF sur Google Drive"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.375rem',
                    borderRadius: '0.375rem',
                    color: colors.status.success,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.background.paleGreen}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Eye size={18} />
                </button>
              )}

              {/* Bouton Upload Drive/Dropbox (Interne) */}
              {(() => {
                const canDownload = statut === 'A envoyer';
                if (!canDownload) return null;

                return (
                  <button
                    onClick={() => handlers.onDownloadInterne(facture)}
                    title="Uploader sur Drive et Dropbox"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: colors.primary.gray,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightGray}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Download size={18} />
                  </button>
                );
              })()}

              {/* Bouton Gérer Paiements (Interne) */}
              {(() => {
                // Afficher pour les factures "Emise" ou "Payée" (consultation)
                if (statut !== 'Emise' && statut !== 'Payée') {
                  return null;
                }

                const isConsultationOnly = statut === 'Payée' || resteAPayer === 0;
                const buttonTitle = isConsultationOnly ? 'Consulter les paiements' : 'Gérer les paiements';

                return (
                  <button
                    onClick={() => handlers.onPaymentHistory(facture)}
                    title={buttonTitle}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.375rem',
                      borderRadius: '0.375rem',
                      color: isConsultationOnly ? colors.primary.teal : colors.primary.gold,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isConsultationOnly ? colors.background.paleGreen : colors.background.paleYellow}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <DollarSign size={18} />
                  </button>
                );
              })()}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default React.memo(FactureRow);
